import { useContext, useState, useMemo } from "react";
import { Panel } from "primereact/panel";
import { OrderList } from "primereact/orderlist";
import { ModuleMultiSelect } from '../module-multiselect';
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";

import { CourseManagementDataContext } from "../../providers/course-management-provider";
import { CourseModuleDto } from "../../general/dtos";
import { ModuleTypeIcon } from '../module-type-icon';

export function CourseEditorModulesTab() {
    const ctx = useContext(CourseManagementDataContext);
    const { selectedCourseFull, updateCourseModules, updateModulePrerequisites } = ctx!;

    const course = ctx?.selectedCourseFull ?? null;

    const [selectedModuleKey, setSelectedModuleKey] = useState<string | null>(null);

    const selectedModule = useMemo(() => {
        return course?.modules.find(m => m.module_key === selectedModuleKey) ?? null;
    }, [course, selectedModuleKey]);

    const moduleLookup = useMemo(() => {
        const map = new Map();
        ctx?.allModules.forEach(m => map.set(m.module_key, m));
        return map;
    }, [ctx?.allModules]);

    const prerequisiteOptions = useMemo(() => {
        if (!course || !selectedModuleKey) return [];
        return course.modules
            .filter(m => m.module_key !== selectedModuleKey)
            .map(m => ({
                label: moduleLookup.get(m.module_key)?.name ?? m.module_key,
                value: m.module_key,
                managed: moduleLookup.get(m.module_key)?.is_managed
            }));
    }, [course, selectedModuleKey, moduleLookup]);

    if (!selectedCourseFull) {
        return <div className="text-gray-500 p-3">No course selected.</div>;
    }

    // Current prerequisite keys
    const selectedPrereqKeys = selectedModule?.prerequisites.map(p => p.module_key) ?? [];

    return (
        <div className="flex gap-4 h-full">

            {/* Left: Module ordering */}
            <div className="w-4 flex flex-column">
                <Panel header="Module order" className="flex flex-column flex-grow-1">
                    <OrderList
                        dataKey="module_key"
                        value={course?.modules}
                        onChange={(e) => {
                            const newOrder = e.value.map((m: any, index: number) => ({
                                ...m,
                                position: index + 1
                            }));
                            updateCourseModules(newOrder.map((m: CourseModuleDto) => m.module_key));
                        }}
                        itemTemplate={(item) => {
                            const full = moduleLookup.get(item.module_key);
                            return (
                                <div
                                    className={`p-2 cursor-pointer ${selectedModuleKey === item.module_key ? "bg-primary-100" : ""}`}
                                    onClick={() => setSelectedModuleKey(item.module_key)}
                                >
                                    <strong className="inline-flex align-items-center gap-2">{full && <ModuleTypeIcon managed={full.is_managed} />}{full?.name ?? item.module_key}</strong>
                                </div>
                            );
                        }}
                        className="flex-grow-1"
                    />
                </Panel>
            </div>

            {/* Right: Dependency editor */}
            <div className="flex-grow-1 flex flex-column">
                <Panel header="Module dependencies" className="flex flex-column flex-grow-1">

                    {!selectedModule && (
                        <div className="text-gray-500">Select a module to edit its dependencies.</div>
                    )}

                    {selectedModule && (
                        <div className="flex flex-column gap-4">

                            {/* Prerequisite selector */}
                            <div className="flex flex-column gap-1">
                                <label className="font-semibold">Prerequisite modules</label>
                                <ModuleMultiSelect
                                    value={selectedPrereqKeys}
                                    options={prerequisiteOptions}
                                    onChange={(keys) => {
                                        updateModulePrerequisites(selectedModuleKey!, keys);
                                    }}
                                    placeholder="Select prerequisite modules"
                                    className="w-full"
                                    display="chip"
                                />
                            </div>

                            {/* Optional/required toggles */}
                            <div className="flex flex-column gap-2">
                                {selectedModule.prerequisites.map((p, idx) => {
                                    const full = moduleLookup.get(p.module_key);
                                    return (
                                        <div key={idx} className="flex align-items-center gap-2">
                                            <strong className="inline-flex align-items-center gap-2">{full && <ModuleTypeIcon managed={full.is_managed} />}{full?.name ?? p.module_key}</strong>

                                            <Checkbox
                                                inputId={`opt-${idx}`}
                                                checked={p.is_optional}
                                                onChange={(e) => {
                                                    updateModulePrerequisites(
                                                        selectedModuleKey!,
                                                        selectedPrereqKeys,
                                                        p.module_key,
                                                        e.checked
                                                    );
                                                }}
                                            />
                                            <label htmlFor={`opt-${idx}`}>
                                                {p.is_optional ? (
                                                    <Tag value="optional" severity="warning" />
                                                ) : (
                                                    <Tag value="required" severity="info" />
                                                )}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    )}
                </Panel>
            </div>

        </div>
    );
}
